<template>
  <div class="autocomplete" ref="root">
    <p v-if="label" class="tw:font-bold">{{ label }}</p>
    <div class="tw:relative">
      <span
        ref="editableSpan"
        contenteditable="true"
        :id="id"
        :aria-label="ariaLabel"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="isOpen.toString()"
        aria-controls="autocomplete-results"
        data-testid="autocomplete-input"
        :class="`${sizeClass} tw:border-gray-500`"
        class="tw:appearance-none tw:relative z-index-10 tw:border tw:w-full tw:focus:outline-0 tw:text-gray-800 tw:focus:border-blue-800 tw:py-2 tw:rounded-lg tw:bg-white tw:inline-block tw:min-w-[200px]"
        @input="onChange"
        @paste="handlePaste"
        @focus="showResultsOnFocus"
        @click="onSpanClick"
      />

      <div
        v-if="showExampleText"
        :class="`${sizingPosition} tw:bg-transparent tw:absolute tw:text-gray-300 tw:pr-4 tw:py-2 tw:rounded-lg tw:pointer-events-none`"
      >
        {{ tempCurrentValue }}
      </div>

      <Search
        v-if="!isOpen || searchValue.length === 0"
        class="tw:absolute tw:right-[10px] tw:pointer-events-none tw:size-5"
        :class="size === 'medium' ? 'tw:top-[10px]' : 'tw:top-[14px]'"
      />

      <button
        v-show="searchValue.length > 0 && isOpen"
        @mousedown.prevent="resetSelection"
        type="button"
        aria-label="Clear search"
        class="tw:absolute tw:right-[10px] tw:bg-transparent tw:border-0 tw:cursor-pointer tw:p-1"
        :class="size === 'medium' ? 'tw:top-[12px]' : 'tw:top-[16px]'"
      >
        <Close class="tw:fill-gray-700 tw:size-3.5" />
      </button>
    </div>

    <small v-if="error" class="tw:text-red-700">{{ error }}</small>

    <BaseCard
      v-show="isOpen"
      id="autocomplete-results"
      no-padding
      class="autocomplete-results tw:max-h-[250px] tw:overflow-auto tw:absolute tw:left-0 tw:right-0 tw:z-9999999"
      :class="label ? 'tw:top-[65px]' : 'tw:top-[54px]'"
    >
      <ul data-testid="autocomplete-results-list">
        <li v-if="isLoading" class="tw:p-2 tw:min-h-[30px]">
          {{ $t('appTileList.loading') }}
        </li>
        <li v-else-if="suggestedOptions.length === 0" class="tw:p-2 tw:min-h-[30px]">
          {{ $t('no_results') }}
        </li>
        <li
          v-for="result in suggestedOptions"
          v-else
          :key="result.value"
          :data-testid="`autocomplete-option-${result.value}`"
          :class="{ 'tw:font-bold': (result.withTranslation ? $t(result.name) : result.name) === value }"
          class="autocomplete-result tw:p-2 tw:px-6 tw:hover:cursor-pointer tw:hover:bg-gray-100 tw:border-b tw:border-gray-100"
          @mousedown.prevent="setResult(result)"
        >
          {{ result.withTranslation ? $t(result.name) : result.name }}
        </li>
      </ul>
    </BaseCard>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { cloneDeep } from '@/utils/Utility'

import BaseCard from '@/components/dashboard/BaseCard.vue'
import Search from '@/components/icons/Search.svg?component'
import Close from '@/components/icons/Close.svg?component'

const { t } = useI18n()
const props = defineProps({
  id: { type: String, default: 'autocomplete' },
  label: { type: String, required: false, default: '' },
  ariaLabel: { type: String, required: true },
  value: { type: String, required: true },
  type: { type: String, required: false, default: 'text' },
  error: { type: [Boolean, String], default: false },
  size: { type: String, default: 'medium' },
  options: { type: Array, default: () => [] },
  isAsync: { type: Boolean, required: false, default: false },
})

const emit = defineEmits(['onSelect'])
const root = ref(null)
const editableSpan = ref(null)
const isOpen = ref(false)
const isLoading = ref(false)
const suggestedOptions = ref(cloneDeep(props.options))
const searchValue = ref(cloneDeep(props.value))
const tempCurrentValue = ref(cloneDeep(props.value))
const showExampleText = ref(false)

const sizeClass = computed(() => {
  if (props.size === 'medium') return 'tw:px-4 box-size-36 tw:text-sm tw:rounded-lg'
  else return 'tw:px-6 box-size-44 tw:text-base tw:rounded-lg'
})

const sizingPosition = computed(() => {
  if (props.size === 'medium') return 'tw:right-1 tw:top-[1px] tw:bottom-1 tw:left-[18px] tw:text-sm'
  else return 'tw:right-1 tw:top-[2px] tw:bottom-1 tw:left-[25px] tw:text-base'
})

watch(
  () => props.options,
  (newOptions, oldOptions) => {
    if (newOptions.length !== oldOptions.length) {
      suggestedOptions.value = newOptions
      isLoading.value = false
    }
  }
)

watch(
  () => props.value,
  newValue => {
    if (editableSpan.value && document.activeElement !== editableSpan.value) {
      editableSpan.value.textContent = newValue
      searchValue.value = newValue
    }
  }
)

watch(searchValue, newVal => {
  if (isOpen.value) {
    showExampleText.value = newVal === ''
  } else {
    showExampleText.value = false
  }
})

onMounted(() => {
  if (editableSpan.value && props.value) {
    editableSpan.value.textContent = props.value
  }

  document.addEventListener('mousedown', handleClickOutside, true)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside, true)
})

const handleClickOutside = evt => {
  if (!root.value || !isOpen.value) return

  const clickedInside = root.value.contains(evt.target)

  if (!clickedInside) {
    closeDropdown()
  }
}

const closeDropdown = () => {
  isOpen.value = false
  showExampleText.value = false

  if (searchValue.value === '') {
    const defaultValue = props.options.find(item => {
      const name = item.withTranslation ? t(item.name) : item.name
      return name === props.value
    })

    if (defaultValue) {
      setResult(defaultValue)
    } else {
      if (editableSpan.value) {
        editableSpan.value.textContent = props.value
      }
      searchValue.value = props.value
    }
  }
}

const resetSelection = () => {
  searchValue.value = ''
  suggestedOptions.value = cloneDeep(props.options)
  isOpen.value = true

  if (editableSpan.value) {
    editableSpan.value.textContent = ''
    setTimeout(() => editableSpan.value?.focus(), 0)
  }

  showExampleText.value = true
}

const onChange = e => {
  searchValue.value = e.target.textContent

  if (props.isAsync) {
    isLoading.value = true
  } else {
    filterResults()
    isOpen.value = true
  }
}

const filterResults = () => {
  suggestedOptions.value = props.options.filter(item => {
    const name = item.withTranslation ? t(item.name) : item.name
    return name.toLowerCase().indexOf(searchValue.value.toLowerCase()) > -1
  })
}

const setResult = result => {
  emit('onSelect', result)
  const text = result.withTranslation ? t(result.name) : result.name
  searchValue.value = text
  isOpen.value = false
  suggestedOptions.value = cloneDeep(props.options)

  if (editableSpan.value) {
    editableSpan.value.textContent = text
    editableSpan.value.blur()
  }

  showExampleText.value = false
}

const showResultsOnFocus = () => {
  if (isOpen.value) return

  tempCurrentValue.value = props.value
  searchValue.value = ''

  if (editableSpan.value) {
    editableSpan.value.textContent = ''
  }

  isOpen.value = true
  showExampleText.value = true
}

const onSpanClick = () => {
  if (!isOpen.value && document.activeElement === editableSpan.value) {
    showResultsOnFocus()
  }
}

const handlePaste = e => {
  e.preventDefault()
  e.stopPropagation()
  return false
}
</script>
